import {
    MAX_TILE_ZOOM,
    MIN_TILE_ZOOM,
    OBSERVED_TILE_VERSION,
    SupportedRegions,
    TILE_SIZE,
    WeatherLayerType,
    type WeatherManifestFrame,
    type WeatherTileManifest,
    tileLat,
    tileLon,
    weatherManifestKey,
    weatherTileKey,
    weatherTileRoute,
} from "@nova/constants"
import { isLocalhostEnvironment } from "@nova/configuration/browser"
import { getApiGatewayDomain } from "@nova/fetch-api-gateway"
import { createExternalValue } from "@nova/toolkit-pure-hooks"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import type { BBox } from "../shared/weatherViewport"
import { createPriorityQueue } from "tools/async/priorityQueue"
import type { FrameImage } from "../shared/frameTextureCache"

export class WeatherFetchError extends Error {
    constructor(readonly status: number) {
        super(`Weather tile fetch failed: ${status}`)
        this.name = "WeatherFetchError"
    }
}

const MAX_TILES = 36
const MAX_CONCURRENT_TILE_FETCHES = 12
const TILE_BYTES = TILE_SIZE * TILE_SIZE * 4

export const MAX_FRAME_BYTES = MAX_TILES * TILE_BYTES

const enqueueTileFetch = createPriorityQueue(MAX_CONCURRENT_TILE_FETCHES)

const ZOOM_LADDER: Record<string, number[]> = {
    precipitation: [4],
    wind10m: [4],
    lightning: [4],
    radar: [4, 6],
}
const zoomLadder = (tileType: WeatherLayerType): number[] =>
    ZOOM_LADDER[tileType] ?? [MAX_TILE_ZOOM]
const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * 60 * 1000
const REFRESH_FRONTIER_MS = HOUR_MS

const FRAME_STRIDES_MS = [0, 12, 30, 60].map((m) => m * MINUTE_MS)

const thinToStride = (times: number[], strideMs: number) => {
    if (strideMs <= 0) return times
    const bySlot = new Map<number, number>()
    for (const t of times) {
        const anchor = Math.round(t / strideMs) * strideMs
        const kept = bySlot.get(anchor)
        if (kept === undefined || Math.abs(t - anchor) < Math.abs(kept - anchor))
            bySlot.set(anchor, t)
    }
    return [...bySlot.values()].sort((a, b) => a - b)
}

export type WeatherFrame = { timestamp: string; image: FrameImage }

export type WeatherView = {
    key: string
    bounds: [number, number, number, number]
    frameBytes: number
}

export type WeatherTileSource = {
    view: (bbox: BBox) => WeatherView
    fetchFrame: (
        bbox: BBox,
        timestamp: string,
        priority: number,
        signal: AbortSignal
    ) => Promise<WeatherFrame | null>
    frameTimes: (startMs: number, endMs: number, strideMs: number) => number[]
    /** Finest cadence whose frames over this range still fit `maxFrames`. */
    frameStrideMs: (startMs: number, endMs: number, maxFrames: number) => number
    ensureFrameIndex: () => Promise<void>
    /** Version the manifest currently names for this frame, or null before the manifest resolves. */
    frameVersion: (hourMs: number) => string | null
}

const lonToTileX = (lon: number, z: number) =>
    Math.min(2 ** z - 1, Math.max(0, Math.floor(((lon + 180) / 360) * 2 ** z)))

const latToTileY = (lat: number, z: number) =>
    Math.min(2 ** z - 1, Math.max(0, Math.floor(((90 - lat) / 180) * 2 ** z)))

const tileCountAt = (bbox: BBox, z: number) => {
    const xMin = lonToTileX(bbox.west, z)
    const xMax = lonToTileX(bbox.east, z)
    const yMin = latToTileY(bbox.north, z)
    const yMax = latToTileY(bbox.south, z)
    return (xMax - xMin + 1) * (yMax - yMin + 1)
}

const pickZoom = (bbox: BBox, ladder: number[]) => {
    const span = Math.max(bbox.east - bbox.west, bbox.north - bbox.south, 1e-3)
    const ideal = Math.round(Math.log2(1440 / span))
    let i = 0
    for (let j = 0; j < ladder.length; j++) {
        if (ladder[j] <= ideal) i = j
    }
    while (i > 0 && tileCountAt(bbox, ladder[i]) > MAX_TILES) i--

    let z = ladder[i]
    while (z > MIN_TILE_ZOOM && tileCountAt(bbox, z) > MAX_TILES) z--
    return z
}

const coveringTiles = (bbox: BBox, z: number) => {
    const xMin = lonToTileX(bbox.west, z)
    const xMax = lonToTileX(bbox.east, z)
    const yMin = latToTileY(bbox.north, z)
    const yMax = latToTileY(bbox.south, z)
    const tiles: Array<{ x: number; y: number }> = []
    for (let y = yMin; y <= yMax; y++) {
        for (let x = xMin; x <= xMax; x++) tiles.push({ x, y })
    }
    return { tiles, xMin, xMax, yMin, yMax }
}

const cycleFallback = (nowMs: number) => {
    const d = new Date(Math.floor(nowMs / (6 * HOUR_MS)) * 6 * HOUR_MS)
    const pad = (n: number): string => String(n).padStart(2, "0")
    return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}${pad(d.getUTCHours())}`
}

const MANIFEST_TTL_MS = 60 * 1000
export const MANIFEST_POLL_MS = MANIFEST_TTL_MS

type Manifest = {
    forecastRun: string | null
    /** Frames whose tiles are confirmed in S3, keyed by valid time in ms. */
    versionByTime: Map<number, string>
}
const EMPTY_MANIFEST: Manifest = { forecastRun: null, versionByTime: new Map() }

const indexFrames = (frames: WeatherManifestFrame[] | undefined) => {
    const byTime = new Map<number, string>()
    for (const f of frames ?? []) {
        const t = Date.parse(f.time)
        if (Number.isFinite(t)) byTime.set(t, f.version)
    }
    return byTime
}

type ManifestEntry = { manifest: Manifest; fetchedAt: number }
const manifestCache = new Map<string, ManifestEntry>()
const manifestInFlight = new Map<string, Promise<Manifest>>()

const manifestGeneration = createExternalValue(0)
export const useWeatherManifestGeneration = manifestGeneration.use

const fetchManifest = (tileType: WeatherLayerType) => {
    const existing = manifestInFlight.get(tileType)
    if (existing) return existing
    const p = (async (): Promise<Manifest> => {
        try {
            const url = await fileManagementApi.getDownloadUrl(weatherManifestKey(tileType), {
                region: SupportedRegions.Canada,
                bucket: "weather",
            })
            const res = await fetch(url, { credentials: "include" })
            if (res.ok) {
                const j = (await res.json()) as WeatherTileManifest
                const manifest: Manifest = {
                    forecastRun: j.forecastRun ?? null,
                    versionByTime: indexFrames(j.frames),
                }
                manifestCache.set(tileType, { manifest, fetchedAt: Date.now() })
                manifestGeneration.update((generation) => generation + 1)
                return manifest
            }
        } catch {}
        return manifestCache.get(tileType)?.manifest ?? EMPTY_MANIFEST
    })().finally(() => manifestInFlight.delete(tileType))
    manifestInFlight.set(tileType, p)
    return p
}

const ensureManifest = (tileType: WeatherLayerType) => {
    const cached = manifestCache.get(tileType)
    if (cached && Date.now() - cached.fetchedAt < MANIFEST_TTL_MS)
        return Promise.resolve(cached.manifest)
    return fetchManifest(tileType)
}

const getCachedManifest = (tileType: WeatherLayerType) =>
    manifestCache.get(tileType)?.manifest ?? null

export const forecastRunMs = (tileType: WeatherLayerType) => {
    const run = getCachedManifest(tileType)?.forecastRun
    const m = run ? /^(\d{4})(\d{2})(\d{2})(\d{2})$/.exec(run) : null
    if (!m) return null
    return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]))
}

const warmFrameTimes = (tileType: WeatherLayerType) => {
    const byTime = getCachedManifest(tileType)?.versionByTime
    if (!byTime || byTime.size === 0) return null
    return [...byTime.keys()].sort((a, b) => a - b)
}

export const publishedForecastHorizonMs = (tileType: WeatherLayerType) => {
    const times = warmFrameTimes(tileType)
    return times ? times[times.length - 1] : null
}

export const publishedHistoryStartMs = (tileType: WeatherLayerType) => {
    const times = warmFrameTimes(tileType)
    return times ? times[0] : null
}

const hourlyTimes = (startMs: number, endMs: number) => {
    const out: number[] = []
    for (let h = Math.ceil(startMs / HOUR_MS) * HOUR_MS; h <= endMs; h += HOUR_MS) out.push(h)
    return out
}

// Localhost never fetches a manifest, so there it has to synthesize the index. A deployed env must
// not: frames the manifest hasn't listed are guaranteed misses.
const frameIndex = (tileType: WeatherLayerType, startMs: number, endMs: number) =>
    warmFrameTimes(tileType) ?? (isLocalhostEnvironment() ? hourlyTimes(startMs, endMs) : [])

/**
 * The manifest names the version each frame's tiles are actually stored under, so this never has to
 * guess. The fallback only covers a frame the client asked for before the manifest listed it.
 */
const cachedFrameVersion = (tileType: WeatherLayerType, hourMs: number): string | null =>
    getCachedManifest(tileType)?.versionByTime.get(hourMs) ?? null

const versionForFrame = async (tileType: WeatherLayerType, timestamp: string, nowMs: number) => {
    if (isLocalhostEnvironment())
        return Date.parse(timestamp) < nowMs - REFRESH_FRONTIER_MS
            ? OBSERVED_TILE_VERSION
            : cycleFallback(nowMs)
    const manifest = await ensureManifest(tileType)
    return (
        manifest.versionByTime.get(Date.parse(timestamp)) ??
        manifest.forecastRun ??
        cycleFallback(nowMs)
    )
}

const MAX_TILE_BITMAPS = 384
const TILE_FRESH_TTL_MS = 5 * 60 * 1000
const TILE_MISS_TTL_MS = 5 * 60 * 1000
type TileEntry = { bitmap: ImageBitmap | null; expiresAt: number }
const tileBitmapCache = new Map<string, TileEntry>()

const getCachedTile = (key: string, nowMs: number) => {
    const hit = tileBitmapCache.get(key)
    if (!hit) return undefined
    if (nowMs >= hit.expiresAt) {
        tileBitmapCache.delete(key)
        return undefined
    }
    tileBitmapCache.delete(key)
    tileBitmapCache.set(key, hit)
    return hit.bitmap
}

const setCachedTile = (key: string, bitmap: ImageBitmap | null, expiresAt: number) => {
    tileBitmapCache.delete(key)
    tileBitmapCache.set(key, { bitmap, expiresAt })
    while (tileBitmapCache.size > MAX_TILE_BITMAPS) {
        const oldest = tileBitmapCache.keys().next().value
        if (oldest === undefined) break
        tileBitmapCache.delete(oldest)
    }
}

export const createTileSource = (tileType: WeatherLayerType): WeatherTileSource => {
    const tileKey = (version: string, time: string, z: number, x: number, y: number) =>
        weatherTileKey({ type: tileType, version, time, z, x, y })

    const renderTileUrl = (version: string, time: string, z: number, x: number, y: number) =>
        `${getApiGatewayDomain()}${weatherTileRoute({ type: tileType, version, time, z, x, y })}`

    const loadTile = (
        version: string,
        time: string,
        z: number,
        x: number,
        y: number,
        priority: number,
        signal: AbortSignal,
        cacheTtlMs: number
    ) => {
        const key = tileKey(version, time, z, x, y)
        const cached = getCachedTile(key, Date.now())
        if (cached !== undefined) return Promise.resolve(cached)
        return enqueueTileFetch(priority, signal, async () => {
            const cache = (bitmap: ImageBitmap): ImageBitmap => {
                setCachedTile(key, bitmap, Date.now() + cacheTtlMs)
                return bitmap
            }
            const cacheMiss = (): null => {
                setCachedTile(key, null, Date.now() + TILE_MISS_TTL_MS)
                return null
            }
            const url = isLocalhostEnvironment()
                ? renderTileUrl(version, time, z, x, y)
                : await fileManagementApi.getDownloadUrl(key, {
                      region: SupportedRegions.Canada,
                      bucket: "weather",
                  })
            const res = await fetch(url, { credentials: "include", signal })
            if (res.status === 204) return cacheMiss()
            if (!res.ok) throw new WeatherFetchError(res.status)
            return cache(await createImageBitmap(await res.blob()))
        })
    }

    const stitchFrame = async (
        bbox: BBox,
        time: string,
        version: string,
        z: number,
        priority: number,
        signal: AbortSignal,
        cacheTtlMs: number
    ) => {
        const { tiles, xMin, xMax, yMin, yMax } = coveringTiles(bbox, z)
        const bitmaps = await Promise.all(
            tiles.map((t) => loadTile(version, time, z, t.x, t.y, priority, signal, cacheTtlMs))
        )
        if (bitmaps.every((b) => b === null)) return null

        const width = (xMax - xMin + 1) * TILE_SIZE
        const height = (yMax - yMin + 1) * TILE_SIZE
        const canvas = new OffscreenCanvas(width, height)
        const ctx = canvas.getContext("2d")
        if (!ctx) return null
        tiles.forEach((t, i) => {
            const bmp = bitmaps[i]
            if (bmp) ctx.drawImage(bmp, (t.x - xMin) * TILE_SIZE, (t.y - yMin) * TILE_SIZE)
        })
        return { timestamp: time, image: { bitmap: canvas.transferToImageBitmap(), width, height } }
    }

    const ladder = zoomLadder(tileType)

    return {
        view: (bbox) => {
            const z = pickZoom(bbox, ladder)
            const { xMin, xMax, yMin, yMax } = coveringTiles(bbox, z)
            const tileCount = (xMax - xMin + 1) * (yMax - yMin + 1)
            return {
                key: `${z}:${xMin}:${xMax}:${yMin}:${yMax}`,
                bounds: [
                    tileLon(xMin, z),
                    tileLat(yMax + 1, z),
                    tileLon(xMax + 1, z),
                    tileLat(yMin, z),
                ] as [number, number, number, number],
                frameBytes: tileCount * TILE_BYTES,
            }
        },
        fetchFrame: async (bbox, timestamp, priority, signal) => {
            const nowMs = Date.now()
            const z = pickZoom(bbox, ladder)
            const version = await versionForFrame(tileType, timestamp, nowMs)
            const cacheTtlMs = version === OBSERVED_TILE_VERSION ? Infinity : TILE_FRESH_TTL_MS
            return stitchFrame(bbox, timestamp, version, z, priority, signal, cacheTtlMs)
        },
        ensureFrameIndex: async () => {
            if (!isLocalhostEnvironment()) await ensureManifest(tileType)
        },
        frameVersion: (hourMs) =>
            isLocalhostEnvironment() ? null : cachedFrameVersion(tileType, hourMs),
        frameStrideMs: (startMs, endMs, maxFrames) => {
            const warm = frameIndex(tileType, startMs, endMs)
            const fits = (strideMs: number) =>
                thinToStride(warm, strideMs).filter((t) => t >= startMs && t <= endMs).length <=
                maxFrames
            return FRAME_STRIDES_MS.find(fits) ?? FRAME_STRIDES_MS[FRAME_STRIDES_MS.length - 1]
        },
        frameTimes: (startMs, endMs, strideMs) => {
            // Thinned before the range filter: slotting a range-clipped list would pick a different
            // frame per slot for the fetch window than for the extent, and never cache-hit.
            return thinToStride(frameIndex(tileType, startMs, endMs), strideMs).filter(
                (t) => t >= startMs && t <= endMs
            )
        },
    }
}
